import React from 'react';
import { string, bool } from 'prop-types';
import { compose } from 'redux';
import { Form as FinalForm } from 'react-final-form';
import classNames from 'classnames';

import { FormattedMessage, injectIntl, intlShape } from '../../../util/reactIntl';
import { propTypes } from '../../../util/types';
import { Form, PrimaryButton, FieldCurrencyInput } from '../../../components';

import css from './Offers.module.css';
import IconPromote from '../../../components/IconPromote/IconPromote';
import appSettings from '../../../config/settings';
import { getPriceValidators } from '../../../util/priceValidators';
import { OFFER_HOURS } from '../../../util/offerWindow';
import { isSellerAwayError } from '../../../util/vacation';

const listingMinimumPriceSubUnits = 0;
const baseCurrency = 'USD';

// Why the offer didn't go: the seller is away, the item is gone, or anything else
const sendErrorMessageId = error =>
  isSellerAwayError(error)
    ? 'OfferForm.sellerAwayError'
    : error?.status === 409
    ? 'OfferForm.listingUnavailableError'
    : 'OfferForm.sendEnquiryError';

const OfferFormComponent = props => (
  <FinalForm
    {...props}
    render={fieldRenderProps => {
      const {
        rootClassName,
        className,
        handleSubmit,
        intl,
        listingTitle,
        authorDisplayName,
        headingId = 'OfferForm.heading',
        headingValues,
        submitId = 'OfferForm.submitButtonText',
        sendEnquiryError,
        invalid,
        inquiryInProgress,
      } = fieldRenderProps;

      const classes = classNames(rootClassName || css.root, className);

      const priceValidators = getPriceValidators(listingMinimumPriceSubUnits, baseCurrency, intl);

      return (
        <Form className={classes} onSubmit={handleSubmit} enforcePagePreloadFor="OrderDetailsPage">
          <IconPromote className={css.icon} />
          <h2 className={css.heading}>
            <FormattedMessage
              id={headingId}
              values={{ hours: OFFER_HOURS, ...(headingValues || { listingTitle, authorDisplayName }) }}
            />
          </h2>
          <FieldCurrencyInput
            id="price"
            name="price"
            className={css.input}
            currencyConfig={appSettings.getCurrencyFormatting('USD')}
            validate={priceValidators}
          />
          <div className={css.actionBtns}>
            {sendEnquiryError ? (
              <p className={css.error}>
                <FormattedMessage id={sendErrorMessageId(sendEnquiryError)} />
              </p>
            ) : null}
            <PrimaryButton
              className={css.submitBtn}
              type="submit"
              inProgress={inquiryInProgress}
              disabled={invalid}
            >
              <FormattedMessage id={submitId} />
            </PrimaryButton>
          </div>
        </Form>
      );
    }}
  />
);

OfferFormComponent.defaultProps = {
  rootClassName: null,
  className: null,
  submitButtonWrapperClassName: null,
  inProgress: false,
  sendEnquiryError: null,
};

OfferFormComponent.propTypes = {
  rootClassName: string,
  className: string,
  submitButtonWrapperClassName: string,

  inProgress: bool,

  // A counter offer names the buyer instead (OffersPage).
  listingTitle: string,
  authorDisplayName: string,
  headingId: string,
  submitId: string,
  sendEnquiryError: propTypes.error,

  // from injectIntl
  intl: intlShape.isRequired,
};

const OfferForm = compose(injectIntl)(OfferFormComponent);

OfferForm.displayName = 'OfferForm';

export default OfferForm;
